import posthog from "posthog-js";

const KEY = (import.meta.env.VITE_POSTHOG_KEY ?? "").trim();
const HOST = (import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com").trim();

let ready = false;
// Identity resolved before PostHog finished loading. The JWT-rehydrate path
// calls identify() within a few hundred ms of init, so without this the whole
// session would lose its user id.
let pendingUserId: string | null = null;

export function initPostHog(): void {
  if (!KEY) return;
  posthog.init(KEY, {
    api_host: HOST,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    disable_session_recording: true,
    loaded: () => {
      ready = true;
      if (pendingUserId) {
        applyIdentity(pendingUserId);
        pendingUserId = null;
      }
    },
  });
}

export interface CaptureFlags {
  /**
   * The call site navigates the tab away right after tracking. Sends over
   * sendBeacon and skips the batch queue so the event survives the unload.
   */
  beforeNavigation?: boolean;
}

export function capturePostHog(
  event: string,
  properties: Record<string, unknown>,
  flags?: CaptureFlags,
): void {
  if (!KEY || !ready) return;
  if (flags?.beforeNavigation) {
    posthog.capture(event, properties, {
      transport: "sendBeacon",
      send_instantly: true,
    });
    return;
  }
  posthog.capture(event, properties);
}

/**
 * Bind the Beconfident user id to this person and to every event captured from
 * here on.
 *
 * Two calls, two purposes: `identify` ties the events to a PostHog person,
 * which is what enables frequency and recurrence per person; the session super
 * property puts `userId` as a plain property on every subsequent event, which
 * is what makes the data straightforward to query.
 *
 * `register_for_session` is deliberate over `register`: it lives in
 * sessionStorage, so the id dies with the tab instead of leaking onto the next
 * person to use the same browser.
 */
export function identifyPostHog(userId: string): void {
  if (!KEY) return;
  if (!ready) {
    pendingUserId = userId;
    return;
  }
  applyIdentity(userId);
}

/** Drop the bound identity. Call when the session ends. */
export function resetPostHog(): void {
  if (!KEY) return;
  pendingUserId = null;
  if (!ready) return;
  posthog.reset();
}

function applyIdentity(userId: string): void {
  posthog.identify(userId);
  posthog.register_for_session({ userId });
}
